import { Middleware, isRejectedWithValue } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { getGeneralizedPath } from './getGeneralizedPath';

const IGNORED_ENDPOINTS = ['getQrcodeTicket'];

const EXTRA_MAX_LENGTH = 5000;
const toReadableJson = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  try {
    const json = JSON.stringify(value, null, 2) ?? String(value);
    return json.length > EXTRA_MAX_LENGTH
      ? `${json.slice(0, EXTRA_MAX_LENGTH)}…(truncated)`
      : json;
  } catch {
    return String(value);
  }
};
// 傳輸層失敗（fetch 本身拋例外）：涵蓋斷線、abort、CORS、TLS/憑證、
// 混合內容、adblock 攔截、後端連不上等。其中 CORS／TLS／後端掛掉屬於
// 可修的部署問題需要上報，但斷線瞬斷會讓頁面上所有 in-flight request
// 同時失敗、一次連發多筆事件，故以下列兩道規則抑制額度消耗：
// 1. navigator.onLine === false（確定離線）時不上報
// 2. 每 TRANSPORT_ERROR_REPORT_INTERVAL_MS 最多上報 1 筆
// 真實事故（如 CORS 壞掉）為全體使用者持續發生，節流後訊號依然足夠。
const TRANSPORT_ERROR_STATUSES = ['FETCH_ERROR', 'TIMEOUT_ERROR'];
const TRANSPORT_ERROR_REPORT_INTERVAL_MS = 60 * 1000;
let lastTransportErrorReportedAt = 0;

// 網路層／連線中斷（含頁面導航中止 in-flight 請求）在各瀏覽器的錯誤字串：
// Chrome「Failed to fetch」、Safari「Load failed」、Firefox「NetworkError」等。
// fetchBaseQuery 讀取 response body 時若連線被中斷，會落在解析階段而回報
// PARSING_ERROR、error 挾帶上述 TypeError；這其實是連線中斷（多為使用者
// 在請求進行中切換頁面），屬環境噪音，應比照傳輸層錯誤節流。
// 真正的 JSON 格式錯誤是 SyntaxError（且 data 為非空原始回應），不符此樣式，
// 仍會照常上報。
const NETWORK_ABORT_ERROR_PATTERN =
  /Load failed|Failed to fetch|NetworkError|network connection was lost|operation was aborted|cancell?ed/i;

const isNetworkAbortParsingError = (
  status: number | string | undefined,
  errorText: unknown,
): boolean =>
  status === 'PARSING_ERROR' &&
  typeof errorText === 'string' &&
  NETWORK_ABORT_ERROR_PATTERN.test(errorText);

// 已知在特定地區（如中國防火長城）會被封鎖的第三方 host。
// 這些 host（Google Cloud Functions）對受影響使用者必定 FETCH_ERROR，
// 屬於無法從應用端修復的環境噪音，故傳輸層失敗時不上報。
// 主 API（api.nanibox3.oneclass.com.tw）不在此列，其傳輸錯誤仍照常上報，
// 以保留真正的 CORS／TLS／後端 outage 訊號。
const GEO_BLOCKED_HOST_SUFFIXES = ['cloudfunctions.net'];

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

const isGeoBlockedHost = (url: string): boolean => {
  const hostname = getHostname(url);
  return (
    hostname !== '' &&
    GEO_BLOCKED_HOST_SUFFIXES.some(suffix => hostname.endsWith(suffix))
  );
};

const shouldReportTransportError = (): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  const now = Date.now();
  if (now - lastTransportErrorReportedAt < TRANSPORT_ERROR_REPORT_INTERVAL_MS) {
    return false;
  }
  lastTransportErrorReportedAt = now;
  return true;
};

type TRejectedAction = {
  type: string;
  payload?: {
    status?: number | string;
    originalStatus?: number;
    data?: unknown;
    error?: string;
  };
  meta?: {
    arg?: { endpointName?: string; originalArgs?: unknown };
    baseQueryMeta?: { request?: { url?: string; method?: string } };
  };
};

export const rtkQueryErrorLogger: Middleware =
  () => next => (action: TRejectedAction) => {
    const endpointName = action.meta?.arg?.endpointName ?? 'unknown';
    const status = action.payload?.status;
    // 後端 5xx/4xx 若回非-JSON body，fetchBaseQuery 會把 503 之類誤判為
    // PARSING_ERROR、真實 HTTP 狀態被埋進 originalStatus。改用真實狀態上報，
    // 讓 5xx 正確聚合、與真正的 JSON 格式錯誤（originalStatus 為 2xx）分開。
    const originalStatus = action.payload?.originalStatus;
    const effectiveStatus =
      status === 'PARSING_ERROR' &&
      typeof originalStatus === 'number' &&
      originalStatus >= 400
        ? originalStatus
        : status;
    // 傳輸層錯誤，或「連線中斷被誤歸類成 PARSING_ERROR」的情況，都比照節流抑制。
    const isTransportError =
      (typeof status === 'string' &&
        TRANSPORT_ERROR_STATUSES.includes(status)) ||
      isNetworkAbortParsingError(status, action.payload?.error);
    const rawUrl = action.meta?.baseQueryMeta?.request?.url ?? '';

    // 傳輸層失敗且 host 是已知被地區封鎖的第三方 host → 視為環境噪音，直接跳過。
    // 放在 shouldReportTransportError() 之前短路，避免噪音吃掉 60s 節流額度，
    // 以免同一分鐘內真正該上報的傳輸錯誤被連帶抑制。
    const isGeoBlockedTransportError =
      isTransportError && isGeoBlockedHost(rawUrl);

    if (
      isRejectedWithValue(action) &&
      !IGNORED_ENDPOINTS.includes(endpointName) &&
      !isGeoBlockedTransportError &&
      (!isTransportError || shouldReportTransportError())
    ) {
      Sentry.withScope(scope => {
        const meta = action.meta;
        const payload = action.payload;

        const method = meta?.baseQueryMeta?.request?.method ?? 'POST';
        const apiPath = rawUrl ? getGeneralizedPath(rawUrl) : endpointName;
        // FETCH_ERROR 時 payload.error 是原始例外字串（如 "TypeError: Failed
        // to fetch"、"AbortError: ..."），是區分斷線／abort／CORS 的關鍵線索
        const errorMessage = payload?.error;

        // Sentry 搜尋用 e.g: "api_path:/auth/login", "api_status:401"
        scope.setTag('type', 'rtk_query_error');
        scope.setTag('api_endpoint', endpointName);
        scope.setTag('api_path', apiPath);
        scope.setTag('api_method', method);
        if (effectiveStatus !== undefined) {
          scope.setTag('api_status', String(effectiveStatus));
        }
        if (errorMessage) {
          // tag 值上限 200 字元，超過會被 Sentry 丟棄
          scope.setTag('api_error', errorMessage.slice(0, 200));
        }

        // 同樣 endpoint + 狀態碼聚合成同一個 issue
        scope.setFingerprint([
          'rtk-query-error',
          endpointName,
          String(effectiveStatus),
        ]);

        scope.setExtra('request_body', toReadableJson(meta?.arg?.originalArgs));
        scope.setExtra('response_body', toReadableJson(payload?.data));

        Sentry.captureMessage(`API Error: ${method} ${apiPath}`);
      });
    }

    return next(action);
  };
